/**
 * MixpanelEditor - Configuration editor for Mixpanel
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  Link,
} from '@mui/material';
import type { MixpanelConfig, PlatformConfig } from '../../types/analytics.types';
import { ANALYTICS_SETTINGS_TEST_IDS } from '@modules/analytics-settings/testIds';

interface MixpanelEditorProps {
  config: PlatformConfig;
  onChange: (config: PlatformConfig, name?: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  showSaveButton?: boolean;
  onSave?: (config: PlatformConfig, name?: string) => void;
  name?: string;
}

export const MixpanelEditor: React.FC<MixpanelEditorProps> = ({
  config,
  onChange,
  disabled = false,
  readOnly = false,
  showSaveButton = false,
  onSave,
  name,
}) => {
  const mixpanelConfig = config as MixpanelConfig;
  const [localConfig, setLocalConfig] = useState<MixpanelConfig>(mixpanelConfig);
  const [localName, setLocalName] = useState(name || '');

  useEffect(() => {
    setLocalConfig(mixpanelConfig);
    setLocalName(name || '');
  }, [mixpanelConfig, name]);

  const handleChange = useCallback(
    (field: keyof MixpanelConfig, value: unknown) => {
      const newConfig = { ...localConfig, [field]: value };
      setLocalConfig(newConfig);
      if (!showSaveButton) {
        onChange(newConfig, localName);
      }
    },
    [localConfig, localName, onChange, showSaveButton]
  );

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(localConfig, localName);
    }
  }, [onSave, localConfig, localName]);

  return (
    <Box>
      <TextField
        label="Project Token"
        value={localConfig.projectToken || ''}
        onChange={(e) => handleChange('projectToken', e.target.value)}
        disabled={disabled || readOnly}
        fullWidth
        required
        sx={{ mb: 2 }}
        helperText={
          <>
            Find your Project Token in{' '}
            <Link href="https://mixpanel.com/settings/project" target="_blank" rel="noopener">
              Mixpanel Project Settings
            </Link>
          </>
        }
        slotProps={{ htmlInput: { 'data-testid': ANALYTICS_SETTINGS_TEST_IDS.editorField('mixpanel', 'projectToken') } }}
      />

      <FormControl fullWidth sx={{ mb: 2 }} data-testid="mixpanel-region-select">
        <InputLabel>Server Zone</InputLabel>
        <Select
          value={localConfig.region || 'US'}
          onChange={(e) => handleChange('region', e.target.value)}
          disabled={disabled || readOnly}
          label="Server Zone"
        >
          <MenuItem value="US">US (api.mixpanel.com)</MenuItem>
          <MenuItem value="EU">EU (api-eu.mixpanel.com)</MenuItem>
          <MenuItem value="IN">IN (api-in.mixpanel.com)</MenuItem>
        </Select>
      </FormControl>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Data Persistence</InputLabel>
        <Select
          value={localConfig.persistence || 'localStorage'}
          onChange={(e) => handleChange('persistence', e.target.value)}
          disabled={disabled || readOnly}
          label="Data Persistence"
        >
          <MenuItem value="localStorage">localStorage (recommended)</MenuItem>
          <MenuItem value="cookie">Cookie</MenuItem>
        </Select>
      </FormControl>

      <FormControlLabel
        control={
          <Switch
            checked={localConfig.trackPageViews ?? true}
            onChange={(e) => handleChange('trackPageViews', e.target.checked)}
            disabled={disabled || readOnly}
          />
        }
        label="Track page views"
      />

      {showSaveButton && (
        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={disabled || !localConfig.projectToken}
            data-testid={ANALYTICS_SETTINGS_TEST_IDS.editorSave('mixpanel')}
          >
            Save
          </Button>
        </Box>
      )}
    </Box>
  );
};
